import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import BasicButton from "../Buttons/BasicButton";
import DefaultDomainCard from "./components/DefaultDomainCard";
import ConnectedDomainsTable from "./components/ConnectedDomainsTable";
import ConnectedDomainsGuide from "./components/ConnectedDomainsGuide";
import DnsInstructionsCard from "./components/DnsInstructionsCard";
import BaseForm from "./StepForm/BaseForm";
import {
    DeleteCustomDomain,
    GetCustomDomainList,
    RetryCustomDomain,
    VerifyCustomDomain,
} from "../../Api/apis/customDomainApi";
import { CustomDomainErrorResponse, CustomDomainListData } from "./types";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";

const ListComponent: React.FC<{
    setIsOnAddDomain: React.Dispatch<React.SetStateAction<boolean>>;
    setVerifyingDomain: React.Dispatch<
        React.SetStateAction<CustomDomainListData | null>
    >;
    domains: CustomDomainListData[];
    refetchDomains: () => Promise<void>;
}> = ({ setIsOnAddDomain, setVerifyingDomain, domains, refetchDomains }) => {
    const { t } = useTranslation();

    const handleRetry = async (domain: CustomDomainListData) => {
        try {
            const resp = await RetryCustomDomain(domain.id);

            toast.success(resp.data.message || t("customDomains.retrySuccess"));
            await refetchDomains();
        } catch (error) {
            if (isAxiosError<CustomDomainErrorResponse>(error)) {
                toast.error(
                    error.response?.data?.message ||
                        t("customDomains.retryFailed"),
                );
            } else {
                toast.error(t("customDomains.retryFailed"));
            }
        }
    };

    const handleRemove = async (domain: CustomDomainListData) => {
        try {
            const resp = await DeleteCustomDomain(domain.id);

            toast.success(resp.data.message || t("customDomains.retrySuccess"));
            await refetchDomains();
        } catch (error) {
            if (isAxiosError<CustomDomainErrorResponse>(error)) {
                toast.error(
                    error.response?.data?.message ||
                        t("customDomains.retryFailed"),
                );
            } else {
                toast.error(t("customDomains.retryFailed"));
            }
        }
    };

    const handleCompleteSetup = async (domain: CustomDomainListData) => {
        setVerifyingDomain(domain);
        setIsOnAddDomain(true);
    };

    const handleVerify = async (domain: CustomDomainListData) => {
        try {
            const resp = await VerifyCustomDomain(domain.id);
            toast.success(
                resp.data.message || t("customDomains.verifySuccessFallback"),
            );
            await refetchDomains();
        } catch (error) {
            if (isAxiosError<CustomDomainErrorResponse>(error)) {
                toast.error(
                    error.response?.data?.message ||
                        t("customDomains.verifyFailedFallback"),
                );
            } else {
                toast.error(t("customDomains.verifyFailedFallback"));
            }
        }
    };

    return (
        <>
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
                <div>
                    <h3 className="fs-3 fw-bold text-dark mb-2">
                        {t("userProfile.customDomains")}
                    </h3>
                    <h4 className="text-muted small mb-0">
                        {t("userProfile.customDomainsSubtitle")}
                    </h4>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <BasicButton
                        size="sm"
                        onClick={() => setIsOnAddDomain(true)}
                    >
                        <FaPlus className="me-2" />
                        {t("customDomains.addDomain")}
                    </BasicButton>
                </div>
            </div>
            {/* NOTE: Might be needed in the future if sellers get their own default domain */}
            {/* <DefaultDomainCard
                domain={mockDefaultDomain.domain}
                visitUrl={mockDefaultDomain.visitUrl}
            /> */}

            <ConnectedDomainsGuide />

            {/* <ConnectedDomainsTable domains={mockConnectedDomains} /> */}
            <ConnectedDomainsTable
                domains={domains}
                onRetry={handleRetry}
                onRemove={handleRemove}
                onVerify={handleVerify}
                onCompleteSetup={handleCompleteSetup}
            />
            {/* NOTE: I don't know if it is actually needed, so I've commented it out for now. */}
            {/* <DnsInstructionsCard
                domain="checkout.acmebrand.net"
                records={mockDnsRecords}
            /> */}
        </>
    );
};

const CustomDomainsPage: React.FC = () => {
    const { t } = useTranslation();

    const [customDomainList, setCustomDomainList] = useState<
        CustomDomainListData[]
    >([]);

    const [isOnAddDomain, setIsOnAddDomain] = useState(false);
    const [verifyingDomain, setVerifyingDomain] =
        useState<CustomDomainListData | null>(null);

    const fetchDomains = useCallback(async () => {
        const resp = await GetCustomDomainList();
        setCustomDomainList(resp.data.data);
    }, []);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    return (
        <div>
            {!isOnAddDomain ? (
                <ListComponent
                    setIsOnAddDomain={setIsOnAddDomain}
                    setVerifyingDomain={setVerifyingDomain}
                    domains={customDomainList}
                    refetchDomains={fetchDomains}
                />
            ) : (
                <BaseForm
                    initialDomain={verifyingDomain ?? undefined}
                    goBackToDomainList={() => {
                        setIsOnAddDomain(false);
                        setVerifyingDomain(null);
                        fetchDomains();
                    }}
                />
            )}
        </div>
    );
};

export default CustomDomainsPage;
